import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2 } from 'lucide-react';
interface LocationData {
  city: string;
  country: string;
  countryCode: string;
}
const NAMES_BY_COUNTRY: Record<string, string[]> = {
  MX: ['Guadalupe', 'Carlos', 'Fernanda', 'José', 'Valentina', 'Miguel', 'Sofía', 'Andrés', 'Camila', 'Roberto'],
  CO: ['Catalina', 'Santiago', 'Daniela', 'Sebastián', 'Laura', 'Alejandro', 'Natalia', 'David', 'Paola', 'Julián'],
  CL: ['Francisca', 'Matías', 'Constanza', 'Tomás', 'Javiera', 'Nicolás', 'Isidora', 'Benjamín', 'Antonia', 'Felipe'],
  PE: ['Milagros', 'Luis', 'Valeria', 'Jorge', 'Lucero', 'César', 'Fiorella', 'Renato', 'Ariana', 'Diego'],
  ES: ['Carmen', 'Pablo', 'Lucía', 'Álvaro', 'Marta', 'Hugo', 'Elena', 'Daniel', 'Sara', 'Adrián'],
  UY: ['Agustina', 'Facundo', 'Martina', 'Gonzalo', 'Camila', 'Joaquín', 'Valentina', 'Ignacio', 'Florencia', 'Mateo'],
  EC: ['Gabriela', 'Andrés', 'María José', 'Sebastián', 'Daniela', 'Cristian', 'Pamela', 'Kevin', 'Mishell', 'Bryan'],
  DEFAULT: ['María', 'Carlos', 'Lucía', 'Pedro', 'Ana', 'Diego', 'Valentina', 'Martín', 'Sofía', 'Andrés']
};
const CITIES_BY_COUNTRY: Record<string, string[]> = {
  MX: ['Ciudad de México', 'Guadalajara', 'Monterrey', 'Puebla', 'Querétaro', 'Mérida', 'Cancún', 'León'],
  CO: ['Bogotá', 'Medellín', 'Cali', 'Barranquilla', 'Cartagena', 'Bucaramanga', 'Pereira', 'Santa Marta'],
  CL: ['Santiago', 'Valparaíso', 'Concepción', 'La Serena', 'Temuco', 'Antofagasta', 'Viña del Mar', 'Rancagua'],
  PE: ['Lima', 'Arequipa', 'Trujillo', 'Cusco', 'Chiclayo', 'Piura', 'Huancayo', 'Iquitos'],
  ES: ['Madrid', 'Barcelona', 'Valencia', 'Sevilla', 'Bilbao', 'Málaga', 'Zaragoza', 'Alicante'],
  UY: ['Montevideo', 'Salto', 'Paysandú', 'Las Piedras', 'Rivera', 'Maldonado', 'Tacuarembó', 'Melo'],
  EC: ['Quito', 'Guayaquil', 'Cuenca', 'Ambato', 'Manta', 'Riobamba', 'Loja', 'Machala'],
  DEFAULT: ['Ciudad de México', 'Bogotá', 'Santiago', 'Lima', 'Madrid', 'Montevideo', 'Quito', 'Guayaquil']
};
const TIMES = ['2 minutos', '3 minutos', '5 minutos', '6 minutos', '7 minutos', '9 minutos', '12 minutos', '15 minutos'];
function generatePurchases(countryCode: string, detectedCity?: string) {
  const code = NAMES_BY_COUNTRY[countryCode] ? countryCode : 'DEFAULT';
  const names = NAMES_BY_COUNTRY[code];
  const cities = CITIES_BY_COUNTRY[code];
  return names.map((name, i) => ({
    name,
    city: i === 0 && detectedCity ? detectedCity : cities[i % cities.length],
    time: TIMES[i % TIMES.length]
  }));
}
export function SocialProofPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDismissed, setIsDismissed] = useState(false);
  const [purchases, setPurchases] = useState(generatePurchases('DEFAULT'));
  // Fetch visitor location on mount
  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const response = await fetch('https://ip-api.com/json/?fields=city,country,countryCode');
        if (response.ok) {
          const data: LocationData = await response.json();
          if (data.countryCode) {
            setPurchases(generatePurchases(data.countryCode, data.city));
          }
        }
      } catch {
        // Fallback to default
        setPurchases(generatePurchases('DEFAULT'));
      }
    };
    fetchLocation();
  }, []);
  useEffect(() => {
    if (isDismissed) return;
    const initialTimeout = setTimeout(() => {
      setIsVisible(true);
    }, 5000);
    const cycleInterval = setInterval(() => {
      if (isDismissed) return;
      setIsVisible(false);
      setTimeout(() => {
        if (isDismissed) return;
        setCurrentIndex(prev => (prev + 1) % purchases.length);
        setIsVisible(true);
      }, 2000);
    }, 8000);
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(cycleInterval);
    };
  }, [isDismissed, purchases.length]);
  useEffect(() => {
    if (isVisible && !isDismissed) {
      const hideTimeout = setTimeout(() => {
        setIsVisible(false);
      }, 5000);
      return () => clearTimeout(hideTimeout);
    }
  }, [isVisible, isDismissed]);
  if (isDismissed) return null;
  const currentPurchase = purchases[currentIndex];
  return <AnimatePresence data-id="element-170">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: 50,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 20,
      x: -20
    }} transition={{
      duration: 0.5
    }} className="fixed bottom-4 left-4 z-50 max-w-xs w-full" data-id="element-171">
          <div className="bg-white rounded-lg shadow-2xl border border-gray-200 p-4 flex items-start gap-3 relative overflow-hidden" data-id="element-172">
            <button onClick={() => setIsDismissed(true)} className="absolute top-1 right-1 text-gray-400 hover:text-gray-600 p-1" data-id="element-173">
              <X size={14} data-id="element-174" />
            </button>

            <div className="flex-shrink-0 relative" data-id="element-175">
              <img src="https://cdn.magicpatterns.com/uploads/tCD7ZagW93aRHqZkHBHRsM/MOCKUPS_FILOSOFIA.png" alt="Product" className="w-12 h-12 object-cover rounded-md border border-gray-100" data-id="element-176" />
              <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5 border-2 border-white" data-id="element-177">
                <CheckCircle2 size={10} className="text-white" data-id="element-178" />
              </div>
            </div>

            <div className="flex-1 pr-4" data-id="element-179">
              <p className="text-sm font-bold text-gray-800 leading-tight" data-id="element-180">
                {currentPurchase.name} de {currentPurchase.city}
              </p>
              <p className="text-xs text-gray-600 mt-0.5" data-id="element-181">
                compró el Kit de Filosofía hace{' '}
                <span className="font-medium text-green-600" data-id="element-182">
                  {currentPurchase.time}
                </span>
              </p>
              <div className="flex items-center mt-1" data-id="element-183">
                <div className="flex" data-id="element-184">
                  {[1, 2, 3, 4, 5].map(i => <span key={i} className="text-[#fbbf24] text-[10px]" data-id="element-185">
                      ★
                    </span>)}
                </div>
                <span className="text-[10px] text-gray-400 ml-1" data-id="element-186">
                  Verificado
                </span>
              </div>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}